import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { MonthlyCostItemTypeDashboardDto } from 'api/dashboard/dtos/MonthlyCostDashboardDto';
import { ConsumesDashboardDto } from 'api/dashboard/dtos/ConsumesDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import Utils from 'common/services/Utils';
import dayjs from 'dayjs';
import Box, { Props as BoxProps } from 'common/components/box/Box';
import ConsumesChart from './ConsumesChart';
import { ReactComponent as ArrowDown } from 'assets/svg/chevron_down.svg';
import Button from 'common/components/button/Button';
import Colors from 'styles/export/colors.module.scss'
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import GenericTypesService from 'api/genericTypes/GenericTypesService';
import { GenericType } from 'api/genericTypes/enums/GenericType';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import styles from './ConsumesChartBox.module.scss';

type Props = {
	// Legacy props (direct data passing)
	data?: ConsumesDashboardDto | null;
	isLoading?: boolean;
	// New props (self-fetch mode)
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
} & Partial<BoxProps>;

const ConsumesChartBox = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<ConsumesDashboardDto | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [selectedYearIndex, setSelectedYearIndex] = useState<number | null>(null);

	const isLegacyMode = props.data !== undefined;
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('consumesChart');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());
	const isControlled = props.filters != null;
	const effectiveDateFrom = props.filters?.dateFrom ?? dateFrom;
	const effectiveDateTo = props.filters?.dateTo ?? dateTo;
	const fuelTypeIdRef = useRef<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		GenericTypesService.catalogCharge()
			.then(types => {
				if (cancelled) return;
				const fuel = (types || []).find(x => x.label === GenericType.FUEL || x.name === GenericType.FUEL);
				fuelTypeIdRef.current = fuel?.value ?? null;
			})
			.catch(() => { /* ignore */ });
		return () => { cancelled = true; };
	}, []);

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getConsumes(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, dateFrom, dateTo]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const allYears = Utils.flatten<MonthlyCostItemTypeDashboardDto>((data?.items || []).map(x => x.items)).map(x =>
		x.date.getFullYear(),
	);
	const yearsOptions = allYears
		.filter((year, i, self) => self.findIndex(d => d === year) === i)
		.sort((a, b) => a - b);
	const existsMoreThanOneYear = allYears.every((year, _, arr) => year === arr[0]) === false;

	const selectedYear = selectedYearIndex !== null ? yearsOptions[selectedYearIndex] : null;

	useEffect(() => {
		if (existsMoreThanOneYear) {
			const currYear = dayjs().year();
			const currYearExistIndex = yearsOptions.findIndex(year => year === currYear);

			setSelectedYearIndex((currYearExistIndex !== null && currYearExistIndex >= 0) ? currYearExistIndex : (yearsOptions.length - 1));
		} else {
			setSelectedYearIndex(null);
		}
	}, [data]);

	if (!data && !isLoading) {
		return null;
	}

	const onPreviousYear = () => {
		if (selectedYearIndex !== null && selectedYearIndex > 0) {
			setSelectedYearIndex(selectedYearIndex - 1);
		}
	};

	const onNextYear = () => {
		if (selectedYearIndex !== null && selectedYearIndex < yearsOptions.length - 1) {
			setSelectedYearIndex(selectedYearIndex + 1);
		}
	};

	const yearOptions = existsMoreThanOneYear && selectedYear
		? (
			<div className={styles.optionsContainer}>
				<Button preset="primary" size="extraSmall" bw={false} onClick={onPreviousYear} disabled={selectedYear === yearsOptions[0]}>
					<ArrowDown width={12} height={12} fill={Colors.white} style={{ transform: 'rotate(90deg)' }} />
				</Button>
				<div className={styles.year}>
					{selectedYear}
				</div>
				<Button preset="primary" size="extraSmall" bw={false} onClick={onNextYear} disabled={selectedYear === yearsOptions[yearsOptions.length - 1]}>
					<ArrowDown width={12} height={12} fill={Colors.white} style={{ transform: 'rotate(-90deg)' }} />
				</Button>
			</div>
		)
		: undefined;

	const content = isLoading ? <div className={styles.centerAlign}> <LoadingSpinner /></div> : data && <ConsumesChart data={data} selectedYear={isLegacyMode ? selectedYear : null} />;

	if (isLegacyMode) {
		return (
			<Box
				className={props.className}
				bodyClassName={props.bodyClassName}
				title={props.title}
				renderOptions={yearOptions}
			>
				{content}
			</Box>
		);
	}

	const periodTotal = (data?.items || []).reduce(
		(acc, typeItem) => acc + typeItem.items.reduce((s, x) => s + (x.value || 0), 0),
		0,
	);

	const goToFuelCharges = async () => {
		let typeId = fuelTypeIdRef.current;
		if (!typeId) {
			try {
				const types = await GenericTypesService.catalogCharge();
				const fuel = (types || []).find(x => x.label === GenericType.FUEL || x.name === GenericType.FUEL);
				typeId = fuel?.value ?? null;
				fuelTypeIdRef.current = typeId;
			} catch {
				// ignore
			}
		}
		try {
			const next: Record<string, unknown> = {
				startDate: effectiveDateFrom,
				endDate: effectiveDateTo,
				vehiclesInactive: false,
			};
			if (typeId) {
				next.typeId = typeId;
				next.type = GenericType.FUEL;
				next.typeName = t('common.generic_types.FUEL' as any);
			}
			localStorage.setItem('CHARGESFILTER-0', JSON.stringify(next));
		} catch {
			// ignore
		}
		window.open('/charges', '_blank');
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.consume') as string}
			subTitleTooltip={t('dashboard.consume_tooltip' as any) as string}
			renderOptions={isControlled ? undefined : <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			<KpiHeader
				value={Utils.formatCurrency(periodTotal)}
				subLabel={t('dashboard.period_total' as any) as string}
				currentTotal={periodTotal}
				previousTotal={null}
				previousRange={null}
				onClick={goToFuelCharges}
			/>
			{content}
		</Box>
	);
};

export default ConsumesChartBox;
